import { useEffect, useState } from 'react';

import Modal from '@v2/shared/components/Modal';
import Button from '@v2/shared/components/Button';
import { Cart, LightboxDeal } from '@v2/shared/lib/types';
import { cn } from '@v2/shared/lib/utils';

import submitForm from './submitForm';

type Props = {
  deal: LightboxDeal;
  /** The cart the deal was priced against — the covers and titles come from its lines. */
  cart: Cart;
  /** A wallet's authorization, when the customer got here by paying with one. */
  paymentPayload: string | null;
  /**
   * Refusing, however it was refused: the decline link, Escape, the close cross, or —
   * on a phone, where the panel no longer fills the screen — the shaded page around it.
   */
  onDecline: () => void;
};

/**
 * How long the offer says it has left. Legacy's countdown module runs for five minutes
 * (`Countdown.lightboxDeal`, 300000ms) and nothing enforces it — it hides itself and the
 * offer stays. Same here.
 */
const COUNTDOWN_SECONDS = 300;

function formatRemaining(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The upgrade offer, shown once, at the moment the customer commits to checking out.
 *
 * This is the "lightbox deal" — the oldest and most valuable thing on the cart: take the
 * magazine you have just decided to buy for a year, and have two. The legacy cart makes
 * it by hijacking the submit of its own form and opening a Fancybox over it. There is no
 * form here, so the drawer calls this instead, and the two agree on what to offer because
 * the offer is decided in one place on the server (`LightboxDealRepository::findDealsForCart`).
 *
 * Both answers post to the legacy cart, which is what applies the upgrade, re-prices the
 * line, records the click and redirects on into checkout. Accepting names the lines the
 * customer kept ticked; refusing names none, and is a request in its own right rather
 * than a silent fall-through, so a refusal is counted.
 *
 * Closing the dialog is refusing. That is legacy's behaviour — its Fancybox `beforeClose`
 * clicks the decline link — and it is the right one: the customer asked to check out, so
 * a dialog they dismissed must not leave them standing in the drawer wondering whether
 * their payment went through. Every route out is therefore the same route: HeadlessUI's
 * `onClose` covers Escape and the shaded page around the panel, and the cross and the
 * decline link are wired to it too.
 *
 * It is `sizeToContentOnMobile` because what the offer is offered against is the sale
 * page, and a full-screen panel hides it. That is also what makes the shade around the
 * panel tappable on a phone for the first time — see `onDecline`.
 */
export default function LightboxDealDialog({ deal, cart, paymentPayload, onDecline }: Props) {
  /*
   * Which upgrades the customer is taking. Every one, until they say otherwise — the
   * legacy checkboxes render `checked` and the single-deal case has no checkbox at all.
   */
  const [selected, setSelected] = useState<number[]>(() => deal.items.map((item) => item.item_id));
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);

  useEffect(() => {
    const timer = setInterval(() => setRemaining((left) => (left > 0 ? left - 1 : 0)), 1000);

    return () => clearInterval(timer);
  }, []);

  const lineFor = (itemId: number) => cart.items?.find((item) => item.id === itemId);
  const multiple = deal.items.length > 1;

  const accept = () => {
    setBusy(true);
    submitForm(deal.apply_url, {
      'xLBIDs[]': selected.map(String),
      ...(paymentPayload ? { xPaymentPayload: paymentPayload } : {}),
    });
  };

  const decline = () => {
    setBusy(true);
    onDecline();
  };

  const toggle = (itemId: number) =>
    setSelected((current) =>
      current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId]
    );

  return (
    <Modal isOpen close={decline} title={deal.title} sizeToContentOnMobile>
      {/*
        The offers scroll and the answer to them does not, at every width.

        There is one offer per magazine in the cart, so a customer who filled it is
        handed a list taller than the panel, and the panel is capped — at the screen
        below `md`, at `md:max-h-[90vh]` above it. Either cap on its own leaves the
        button off the bottom of the scroll: a ten-magazine cart on a 1280x860 desktop
        put it 261px below the viewport, reachable only by scrolling the offers to
        their very end.

        The arrangement is the cart drawer's, a few files over: `flex-grow` on the list
        pushes the actions down when the offer is short, and `sticky bottom-0` holds
        them against the panel's edge once it is not. The white background and the rule
        above them are load-bearing — without them the covers scroll through the button.

        The two boxes add back up to the single `p-4` they were split out of — `pt-4`
        here, `py-4` below, and the button's own `mt-4` given up to the gap the padding
        now makes — so pinning them changes the spacing at neither width.
      */}
      <div className="flex flex-col min-h-full">
        <div className="flex-grow px-4 pt-4">
          {deal.subtitle && <p className="m-0 mb-4 text-slate-600">{deal.subtitle}</p>}

          <ul className="list-none m-0 p-0">
            {deal.items.map((item) => {
              const line = lineFor(item.item_id);
              const image = line?.product.featured_image;

              return (
                <li key={item.item_id} className="flex gap-3 items-center py-3 border-b border-slate-100 last:border-0">
                  {multiple && (
                    <input
                      type="checkbox"
                      className="shrink-0"
                      checked={selected.includes(item.item_id)}
                      onChange={() => toggle(item.item_id)}
                      aria-label={`Upgrade ${line?.product.name || 'this subscription'}`}
                    />
                  )}
                  {image?.url && (
                    <img
                      src={image.url}
                      alt={image.alt_text || line?.product.name || ''}
                      width={54}
                      height={70}
                      className="w-[54px] h-[70px] object-contain shrink-0"
                    />
                  )}
                  <div className="min-w-0">
                    {/*
                      The title, and only where the copy has not already said it.

                      A deal's `text` is a stored tSys template that branches on
                      `deal.multiple`, and the multi-line branch of every deal live today
                      opens with `{deal.product.name} - 2nd Year for Only $x` — because the
                      legacy lightbox draws one combined cover for the whole offer and needs
                      each row to name itself. The single branch says "Add a 2nd Year for an
                      additional $x" and names nothing, so there the title is the only place
                      the magazine appears. Printing it in both cases is what put "Bead &
                      Button" twice in a two-magazine offer.
                    */}
                    {line && !multiple && (
                      <p className="m-0 font-semibold text-sm text-slate-900">{line.product.name}</p>
                    )}
                    {/*
                      The first line carries the weight the title would have, in the
                      mode where the title is not there to carry it. That is the line
                      the magazine's name is in — the multiple branch above opens with
                      it — so leaving it regular gave a row that named itself in the
                      same weight as its own small print.

                      The lines after it stay quiet in both modes. They are the
                      parenthetical — "Save an Extra 20%!", which the multiple branch
                      emits only for a hybrid deal — and single mode already draws its
                      own as `text-sm text-slate-500` beneath a bold title. Bolding
                      those as well would leave the row nothing unemphasised to lean on.
                    */}
                    {item.lines.map((copy, index) => (
                      <p
                        key={copy}
                        className={cn(
                          index === 0
                            ? 'm-0 mt-0.5 text-slate-700'
                            : 'm-0 mt-0.5 text-sm text-slate-500',
                          index === 0 && multiple && 'font-semibold'
                        )}
                      >
                        {copy}
                      </p>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="sticky bottom-0 bg-white px-4 py-4 border-t-[1.5px] border-solid border-x-0 border-b-0 border-slate-200">
          <Button fullWidth disabled={busy || !selected.length} onClick={accept}>
            {deal.accept_label}
          </Button>

          {/*
            A button and not a link, even though the server end of it is a url: it has to
            carry a wallet's payload when there is one, and a GET cannot. It still reads as
            the quiet option, which is the point — legacy draws it as a plain anchor under
            the button.
          */}
          <p className="text-center m-0 mt-3">
            <button
              type="button"
              className="bg-transparent border-0 p-0 underline text-slate-500 cursor-pointer"
              disabled={busy}
              onClick={decline}
            >
              {deal.decline_label}
            </button>
          </p>

          {remaining > 0 && (
            <p className="text-center text-sm text-slate-500 m-0 mt-3">
              This offer expires in <time>{formatRemaining(remaining)}</time>
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
